import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { TokenStorage } from "./storage";

export interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

export interface ApiError {
  status: number;
  message: string;
}

const createAxiosClient = (): AxiosInstance => {
  const client = axios.create({
    withCredentials: false,
    baseURL: `${import.meta.env.VITE_API_URL}/v2/corsi`,
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
    headers: {
      "Content-Type": "application/json",
      "X-App-Id": import.meta.env.VITE_APP_ID,
      "X-Organization-Id": import.meta.env.VITE_ORGANIZATION_ID,

    },
  });

  // Request interceptor per aggiungere il token
  client.interceptors.request.use(
    (config) => {
      const token = TokenStorage.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor per gestire errori
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<unknown>>) => {
      // Se lo status nella risposta indica errore, reject
      if (response.data?.status && response.data.status >= 400) {
        return Promise.reject({
          response: {
            data: response.data,
            status: response.data.status,
          },
        });
      }
      return response;
    },
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config;

      // Se l'errore è 401 e non è già un tentativo di refresh
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest.headers._retry
      ) {
        originalRequest.headers._retry = true;

        try {
          const refreshToken = TokenStorage.getRefreshToken();
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          // TODO: Implementare endpoint refresh quando disponibile
          // Per ora rimuoviamo i token e redirigiamo al login
          TokenStorage.removeTokens();
          window.location.href = "/login";
          return Promise.reject(error);
        } catch (refreshError) {
          TokenStorage.removeTokens();
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createAxiosClient();
